import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  schema
}) => {
  const siteName = 'SilverFox Media';
  const defaultDescription = 'SilverFox Media builds products, internal tools and data platforms, including Palantir Foundry ontologies and OSDK apps. Palantir-certified, 10+ years in software, based in Dallas-Fort Worth.';
  // Canonical host. Must be the real domain, not the Render URL, or Google
  // treats silverfoxmedia.onrender.com as the preferred address.
  const siteUrl = 'https://silverfoxmedia.co';
  // 1200x630 share card; server/seo/routeMeta.js uses the same defaults.
  const defaultImage = `${siteUrl}/images/og/silverfox-default.jpg`;

  const fullTitle = title ? `${title} | ${siteName}` : `${siteName} | Software, Data Platforms & Web Development`;
  const fullDescription = description || defaultDescription;
  const fullImage = image || defaultImage;
  // Only the /images/og/ share cards are known to be 1200x630.
  const isShareCard = fullImage.includes('/images/og/');
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;

  const defaultSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteName,
    url: siteUrl,
    description: defaultDescription
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteName,
    url: siteUrl,
    logo: `${siteUrl}/images/silverfox-logo-mark.webp`,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+1-972-800-8105',
      contactType: 'customer service',
      email: 'information@silverfoxmedia.co',
      areaServed: 'US',
      availableLanguage: 'English'
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Dallas-Fort Worth',
      addressRegion: 'TX',
      addressCountry: 'US'
    }
  };

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:image" content={fullImage} />
      {isShareCard && <meta property="og:image:width" content="1200" />}
      {isShareCard && <meta property="og:image:height" content="630" />}
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={fullImage} />
      <script type="application/ld+json">
        {JSON.stringify(schema || defaultSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
    </Helmet>
  );
};

export default SEO;
